<template>
    <Dialog :visible="showDialog" class="action-popup" :modal="true" :draggable="false">
        <template #container>
            <div class="dialog-header-danger">
                Delete a User
            </div>
            <div class="dialog-content">
                <div class="w-full flex justify-content-center align-items-center mb-3">
                    <SvgWarning className="popup-warning-svg" color="var(--popup-warning-color)"/>
                </div>
                <span class="text align-self-center mb-2 px-1">Are you sure you want to delete the following user?</span>
                <div id="user-to-delete" class="content flex flex-row align-items-center justify-content-center mt-2 mb-4 w-full gap-3">
                    <Avatar :label="getAvatarLabelFromFirstNameAndLastName(userToDelete.first_name, userToDelete.last_name)" size="large" shape="circle"/>
                    <div class="flex flex-column align-items-start justify-content-center">
                        <span style="font-size: var(--font-size-xlarge); font-weight: var(--font-weight-bold);">{{ userToDelete.first_name }} {{ userToDelete.last_name }}</span>
                        <span style="font-size: var(--font-size-large); font-weight: var(--font-weight-xthin);">{{ userToDelete.email }}</span>
                    </div>
                </div>
                <div v-if="licensesWhereDelegate.length>0" class="flex flex-column align-items-center justify-content-center">
                    <span v-if="licensesWhereDelegate.length==1" class="text text-center px-2 sm:px-6 mt-2 mb-3">This user is assigned to the following license and will lose the ability to activate this serial in the future.</span>
                    <span v-else class="text text-center px-2 sm:px-6 mt-2 mb-3">This user is assigned to the following licenses and will lose the ability to activate these serials in the future.</span>
                    <div class="content flex flex-column w-full justify-content-center align-items-center">
                        <DataTable :value="licensesWhereDelegate" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" class="mb-3" scrollable scrollHeight="flex" style="max-height: 25vh; width: 85%">
                            <Column field="serial_number" header="Serial number">
                                <template #body="slotProps">
                                    <span class="text-serial-number">{{ slotProps.data.serial_number }}</span>
                                </template>
                            </Column>
                            <Column field="product.application" header="Application"></Column>
                            <Column field="product.exp" header="Expiration">
                                <template #body="slotProps">
                                    <span v-if="slotProps.data.product.exp === '-1'" class="font-italic">None</span>
                                    <span v-else>{{format(new Date(slotProps.data.product.exp), 'MMM do, yyyy')}}</span>
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </div>
                <div v-else/>
            </div>

            <div class="dialog-footer">
                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="$emit('close')"/>
                    <ButtonModule type="primary" severity="danger" label="Delete" @click="showPasswordVerificationDialog = true"/>
                </div>
            </div>
        </template>
    </Dialog>

    <!-- DIALOGS -->

    <!-- Authentication using PASSWORD -->
    <VerificationAuthenticationDialog :showDialog="showPasswordVerificationDialog" :incorrectPassword="wrongPassword" @cancel="showPasswordVerificationDialog = false;" @ok="(val) => verificationAuthenticationDone(val)" :isLoading="httpDeleteUserLoading"/>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, computed, watchEffect } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog'
import Avatar from 'primevue/avatar'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
// vue
import ButtonModule from '@/components/Modules/ButtonModule.vue'
import VerificationAuthenticationDialog from '@/components/Dialogs/OtherDialogs/VerificationAuthenticationDialog.vue';
import SvgWarning from '@/components/SVGs/SvgWarning.vue';
// Others
import AvatarsUtils from '@/composables/AvatarsUtils'
import UserUtils from '@/composables/UserUtils';
import toastService from '@/composables/toastService';
import { format } from 'date-fns';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()
const { logoutUser } = UserUtils(store, router)
const { getAvatarLabelFromFirstNameAndLastName } = AvatarsUtils()

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchDeleteUser } = useApiRequest()
const httpDeleteUserLoading = ref(false)
const httpDeleteUserData = ref(null)

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    userToDelete: Object,
})
const { userToDelete } = toRefs(props)

// --- EMITS ---
const emit = defineEmits(['close'])

// --- VARIABLES ---
const licensesWhereDelegate = computed(() => {
    return store.state.licenses.licenses.filter((license) => license.delegates.some((delegate) => delegate.email === userToDelete.value.email))
})

// --- PASSWORD VERIFICATION ---
const showPasswordVerificationDialog = ref(false)
const wrongPassword = ref(false)
// Whenever the dialog opens, it should reset the wrongPassword boolean
watchEffect(() => {
    if (showPasswordVerificationDialog.value) { wrongPassword.value = false }
})
const verificationAuthenticationDone = (password) => {
    // IT CALLS THE SUBMIT REQUEST WITH THE PASSWORD AS THE VERIFICATION
    submitDeleteUserConfirmed(password)
}

// --- SUBMIT DELETE USER CONFIRMED ---
const submitDeleteUserConfirmed = async (passwordVerification) => {
    wrongPassword.value = false
    // Send HTTP request with passwordVerification
    httpDeleteUserLoading.value = true
    try {
        httpDeleteUserData.value = await fetchDeleteUser({ password_plain_auth: passwordVerification, user_id: userToDelete.value.id })
        // Reset of values
        showPasswordVerificationDialog.value = false
        wrongPassword.value = false
        // Delete the serial number info from the cache
        store.commit('users/removeUser', httpDeleteUserData.value.user_id)
        store.commit('licenses/forgetDelegate', httpDeleteUserData.value.user_id)
        // Success toast
        //toastService.add({ severity: 'success', summary: "Delete a User: Success", detail: 'The user has been successfully deleted from your list.', life: 5000 });
        // Close
        emit('close')
    } catch (err) {
        handleDeleteUserError(err)
    } finally {
        httpDeleteUserLoading.value = false
    }
}
// Handle an error from the server
const handleDeleteUserError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Delete a User: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Delete a User: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                    toastService.add({ severity: 'warn', summary: "Delete a User: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Delete a User: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // WRONG_PASSWORD / ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID / NOT_AUTHORIZED (401)
                if (errorCode == 'WRONG_PASSWORD') { // WRONG_PASSWORD
                    wrongPassword.value = true
                }
                else if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Delete a User: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND (404)
                if (errorCode == 'ACCOUNT_NOT_FOUND') { // ACCOUNT_NOT_FOUND
                    toastService.add({ severity: 'error', summary: "Delete a User: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Delete a User: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 409: // USER_NOT_DELETABLE (409) => toast
                if (errorCode == 'USER_NOT_DELETABLE') { // USER_NOT_DELETABLE
                    toastService.add({ severity: 'warn', summary: "Delete a User: Not Authorized.", detail: errorMessage, life: 5000 });
                    showPasswordVerificationDialog.value = false
                }
                else {  toastService.add({ severity: 'error', summary: `Delete a User: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR') { // DATABASE_ERROR
                    toastService.add({ severity: 'error', summary: "Delete a User: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                    showPasswordVerificationDialog.value = false
                }
                else {  toastService.add({ severity: 'error', summary: `Delete a User: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Delete a User: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Delete a User: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

</script>

<style scoped>
</style>